import { CustomPanel, DropdownPrimeReact } from "@finanedi.es/storybook"
import {
  faAngleDown,
  faAngleRight,
  faTimes,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MODALIDAD_MODELO_180 } from "api/Types/ModalidadModelo180"
import { SITUACION_INMUEBLE_MODELO_180 } from "api/Types/SituacionInmuebleModelo180"
import { TIPO_NUMERACION } from "api/Types/TipoNumeracion"
import { TipoViaIneOptions } from "api/Types/TipoViaIne"
import { TFunction } from "i18next"
import { InputText } from "primereact/inputtext"
import { useEffect, useState } from "react"
import { FieldErrors, UseFormSetValue } from "react-hook-form"
import { getProvinceIneOptions } from "utils/provinceDropdownOptions"
import {
  FieldTemplateGeneric,
  renderFieldTemplateGeneric,
} from "../../../PlantillasBloquesTabla"
import { MyFormValuesModelo180 } from "./Perceptores180"

const PerceptorsTemplate180 = ({
  t,
  text,
  values,
  setValue,
  perceptorIndex,
  handleDelete,
  errors,
}: {
  t: TFunction
  text: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  perceptorIndex: number
  handleDelete: () => void
  errors: FieldErrors<MyFormValuesModelo180>
}) => {
  const [categoryExpanded, setCategoryExpanded] = useState(true)
  const ANCHOR_REF =
    "https://www1.sedecatastro.gob.es/cycbieninmueble/ovcbusqueda.aspx"

  const fieldsPerceptors: FieldTemplateGeneric[] = [
    {
      id: "row1",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.perceptorNif"),
          code: `perceptores.${perceptorIndex}.nif`,
          value: values?.perceptores?.[perceptorIndex]?.nif,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.legalRepresentative"),
          code: `perceptores.${perceptorIndex}.legalRepresentative`,
          value: values?.perceptores?.[perceptorIndex]?.legalRepresentative,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.perceptorName"),
          code: `perceptores.${perceptorIndex}.denominacion`,
          value: values?.perceptores?.[perceptorIndex]?.denominacion,
          colSpan: "tw-col-span-3",
          inputType: "text",
        },
      ],
    },
    {
      id: "row2",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.ejercicioDevengo"),
          code: `perceptores.${perceptorIndex}.ejercicioDevengo`,
          value: values?.perceptores?.[perceptorIndex]?.ejercicioDevengo,
          colSpan: "tw-col-span-2",
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.codigoProvincia"),
          code: `perceptores.${perceptorIndex}.provincia`,
          value: values?.perceptores?.[perceptorIndex]?.provincia,
          inputType: "dropdown",
          options: getProvinceIneOptions(t),
        },
        {
          label: t("declaracionModelo180.perceptors.modalidad"),
          code: `perceptores.${perceptorIndex}.modalidad`,
          value: values?.perceptores?.[perceptorIndex]?.modalidad,
          colSpan: "tw-col-span-2",
          inputType: "dropdown",
          mandatory: true,
          error: errors?.perceptores?.[perceptorIndex]?.modalidad?.message,
          options: [
            {
              label: t("declaracionModelo180.perceptors.modalidadDineraria"),
              value: MODALIDAD_MODELO_180.DINERARIA,
            },
            {
              label: t("declaracionModelo180.perceptors.modalidadEspecie"),
              value: MODALIDAD_MODELO_180.ESPECIE,
            },
          ],
        },
      ],
    },
    {
      id: "row3",
      inputsOne: [
        {
          label: t("declaracionModelo180.perceptors.baseRetencion"),
          code: `perceptores.${perceptorIndex}.baseRetencion`,
          value: values?.perceptores?.[perceptorIndex]?.baseRetencion,
          colSpan: "tw-col-span-2",
          inputType: "currency",
        },
        {
          label: t("declaracionModelo180.perceptors.porcentajeRetencion"),
          code: `perceptores.${perceptorIndex}.porcentajeRetencion`,
          value: values?.perceptores?.[perceptorIndex]?.porcentajeRetencion,
          inputType: "number",
        },
        {
          label: t("declaracionModelo180.perceptors.retencion"),
          code: `perceptores.${perceptorIndex}.retencion`,
          value: values?.perceptores?.[perceptorIndex]?.retencion,
          colSpan: "tw-col-span-2",
          inputType: "currency",
        },
      ],
    },
  ]

  const fieldsSituacionInmueble = [
    {
      label: t("taxSummary.declaracion.addressForm.situacionInmueble"),
      code: `perceptores.${perceptorIndex}.situacionInmueble`,
      value: values?.perceptores?.[perceptorIndex]?.situacionInmueble,
      options: [
        {
          label: t("declaracionModelo180.perceptors.inmuebleUno"),
          value: SITUACION_INMUEBLE_MODELO_180.EXCEPTO_PAIS_VASCO_Y_NAVARRA,
        },
        {
          label: t("declaracionModelo180.perceptors.inmuebleUno"),
          value: SITUACION_INMUEBLE_MODELO_180.PAIS_VASCO,
        },
        {
          label: t("declaracionModelo180.perceptors.inmuebleTres"),
          value: SITUACION_INMUEBLE_MODELO_180.NAVARRA,
        },
        {
          label: t("declaracionModelo180.perceptors.inmuebleCuatro"),
          value:
            SITUACION_INMUEBLE_MODELO_180.SITUACIONES_ANTERIORES_SIN_REFERENCIA_CATASTRAL,
        },
      ],
      mandatory: true,
      error: errors?.perceptores?.[perceptorIndex]?.situacionInmueble?.message,
    },
    {
      label: t("taxSummary.declaracion.addressForm.referenciaCatastral"),
      code: `perceptores.${perceptorIndex}.referenciaCatastral`,
      value: values?.perceptores?.[perceptorIndex]?.referenciaCatastral,
    },
  ]

  const fieldsPerceptorsTwo: FieldTemplateGeneric[] = [
    {
      id: "row5",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.tipoVia"),
          code: `perceptores.${perceptorIndex}.tipoVia`,
          value: values?.perceptores?.[perceptorIndex]?.tipoVia,
          inputType: "dropdown",
          mandatory: true,
          options: TipoViaIneOptions?.flatMap((option) => ({
            label: option.label.split("_").join(" ").trim(),
            value: option.value,
          })),
          error: errors?.perceptores?.[perceptorIndex]?.tipoVia?.message,
        },
        {
          label: t("taxSummary.declaracion.addressForm.nombreVia"),
          code: `perceptores.${perceptorIndex}.nombreVia`,
          value: values?.perceptores?.[perceptorIndex]?.nombreVia,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.tipoNumeracion"),
          code: `perceptores.${perceptorIndex}.tipoNumeracion`,
          value: values?.perceptores?.[perceptorIndex]?.tipoNumeracion,
          inputType: "dropdown",
          mandatory: true,
          options: [
            {
              label: t("taxSummary.declaracion.addressForm.numero"),
              value: TIPO_NUMERACION.NUMERO,
            },
            {
              label: t("taxSummary.declaracion.addressForm.sinNumero"),
              value: TIPO_NUMERACION.SIN_NUMERO,
            },
            {
              label: t("taxSummary.declaracion.addressForm.kilometro"),
              value: TIPO_NUMERACION.KILOMETRO,
            },
            {
              label: t("taxSummary.declaracion.addressForm.otros"),
              value: TIPO_NUMERACION.OTROS,
            },
          ],
          error: errors?.perceptores?.[perceptorIndex]?.tipoNumeracion?.message,
        },
        {
          label: t("taxSummary.declaracion.addressForm.numeroCasa"),
          code: `perceptores.${perceptorIndex}.numeroCasa`,
          value: values?.perceptores?.[perceptorIndex]?.numeroCasa,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.calificadorNumero"),
          code: `perceptores.${perceptorIndex}.calificadorNumero`,
          value: values?.perceptores?.[perceptorIndex]?.calificadorNumero,
          inputType: "text",
          disabled: false,
        },
      ],
    },
    {
      id: "row6",
      inputsOne: [
        {
          label: t("taxSummary.declaracion.addressForm.bloque"),
          code: `perceptores.${perceptorIndex}.bloque`,
          value: values?.perceptores?.[perceptorIndex]?.bloque,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.portal"),
          code: `perceptores.${perceptorIndex}.portal`,
          value: values?.perceptores?.[perceptorIndex]?.portal,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.escalera"),
          code: `perceptores.${perceptorIndex}.escalera`,
          value: values?.perceptores?.[perceptorIndex]?.escalera,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.plantaPiso"),
          code: `perceptores.${perceptorIndex}.plantaPiso`,
          value: values?.perceptores?.[perceptorIndex]?.plantaPiso,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.puerta"),
          code: `perceptores.${perceptorIndex}.puerta`,
          value: values?.perceptores?.[perceptorIndex]?.puerta,
          inputType: "text",
        },
      ],
    },
    {
      id: "row7",
      inputsOne: [
        {
          label: t("declaracionModelo180.perceptors.complemento"),
          code: `perceptores.${perceptorIndex}.complemento`,
          value: values?.perceptores?.[perceptorIndex]?.complemento,
          inputType: "text",
        },
        {
          label: t("declaracionModelo180.perceptors.localidadPoblacion"),
          code: `perceptores.${perceptorIndex}.localidadPoblacion`,
          value: values?.perceptores?.[perceptorIndex]?.localidadPoblacion,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
        {
          label: t("taxSummary.declaracion.addressForm.municipio"),
          code: `perceptores.${perceptorIndex}.municipio`,
          value: values?.perceptores?.[perceptorIndex]?.municipio,
          inputType: "text",
          colSpan: "tw-col-span-2",
        },
      ],
    },
    {
      id: "row8",
      inputsOne: [
        {
          label: t("declaracionModelo180.perceptors.codigoMunicipio"),
          code: `perceptores.${perceptorIndex}.codigoMunicipio`,
          value: values?.perceptores?.[perceptorIndex]?.codigoMunicipio,
          inputType: "text",
        },
        {
          label: t("taxSummary.declaracion.addressForm.codigoProvincia"),
          code: `perceptores.${perceptorIndex}.provinciaDos`,
          value: values?.perceptores?.[perceptorIndex]?.provinciaDos,
          inputType: "dropdown",
          mandatory: true,
          options: getProvinceIneOptions(t),
          error: errors?.perceptores?.[perceptorIndex]?.provinciaDos?.message,
          colSpan: "tw-col-span-2",
        },
        {
          label: t("taxSummary.declaracion.addressForm.codigoPostal"),
          code: `perceptores.${perceptorIndex}.codigoPostal`,
          value: values?.perceptores?.[perceptorIndex]?.codigoPostal,
          inputType: "text",
        },
      ],
    },
  ]

  const hasFormErrors = errors?.perceptores?.[perceptorIndex]

  useEffect(() => {
    if (hasFormErrors) {
      setCategoryExpanded(false)
    }
  }, [hasFormErrors])

  return (
    <CustomPanel
      title={text}
      expandIcon={null}
      collapseIcon={null}
      className='[&>div:first-child]:tw-cursor-pointer tw-font-medium !tw-shadow-none tw-border [&>div:nth-child(2)]:!tw-max-h-[3000px]'
      collapsed={categoryExpanded}
      onToggle={(e) => setCategoryExpanded(e.value)}
      headerTemplate={() => (
        <div
          className='tw-flex tw-justify-between tw-px-4 tw-py-3'
          onClick={() => setCategoryExpanded((prevState) => !prevState)}
        >
          <p>{text}</p>
          <div>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                handleDelete()
              }}
              className='hover:tw-bg-gray-100 tw-w-6 tw-rounded tw-transition-colors'
            >
              <FontAwesomeIcon
                icon={faTimes}
                className='tw-text-red-500'
              />
            </button>
            <button
              type='button'
              onClick={() => setCategoryExpanded((prevState) => !prevState)}
              className='tw-w-4 tw-ml-4'
            >
              <FontAwesomeIcon
                icon={categoryExpanded ? faAngleDown : faAngleRight}
              />
            </button>
          </div>
        </div>
      )}
    >
      <div className='tw-p-4'>
        {fieldsPerceptors.map((row) => (
          <div
            key={row.id}
            className='tw-w-full tw-h-fit tw-grid tw-grid-cols-5 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'
          >
            {renderFieldTemplateGeneric({
              fields: row.inputsOne,
              setValue,
              t,
            })}
          </div>
        ))}
        <div className='tw-w-full tw-h-fit tw-grid tw-grid-cols-5 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'>
          <div className='tw-col-span-3 tw-items-center'>
            <div className='tw-text-left tw-text-xs tw-font-medium'>
              {fieldsSituacionInmueble[0].label}
            </div>
            <DropdownPrimeReact
              value={fieldsSituacionInmueble[0].value}
              options={fieldsSituacionInmueble[0].options}
              optionLabel='label'
              optionValue='value'
              onChange={(e) =>
                setValue(`${fieldsSituacionInmueble[0].code}`, e.value)
              }
              placeholder='Selecciona'
              className='!tw-w-full !tw-h-10 [&_span]:tw-text-sm'
              panelClassName='tw-rounded-md tw-max-w-100 tw-text-sm'
            />
            {fieldsSituacionInmueble[0].mandatory && (
              <small
                className={`${
                  fieldsSituacionInmueble[0].error
                    ? "tw-text-red-500"
                    : "tw-text-gray-500 "
                }`}
              >
                {t("taxSummary.declaracion.mandatoryField")}
              </small>
            )}
          </div>
          <div className='tw-col-span-2 tw-items-center'>
            <div className='tw-text-left tw-text-xs tw-font-medium'>
              {fieldsSituacionInmueble[1].label}
            </div>
            <InputText
              className='tw-w-full tw-text-sm'
              value={fieldsSituacionInmueble[1].value}
              onChange={(e) =>
                setValue(`${fieldsSituacionInmueble[1].code}`, e.target.value)
              }
            />
            <a
              href={ANCHOR_REF}
              target='_blank'
              rel='noopener noreferrer'
              className='tw-text-left tw-text-xs tw-font-medium tw-text-info hover:tw-underline'
            >
              {t(
                "taxSummary.declaracion.addressForm.buscarReferenciaCatastral"
              )}
            </a>
          </div>
        </div>

        {fieldsPerceptorsTwo.map((row) => (
          <div
            key={row.id}
            className='tw-w-full tw-h-fit tw-grid tw-grid-cols-5 tw-py-3 tw-gap-7 tw-border-b tw-border-neutral-25'
          >
            {renderFieldTemplateGeneric({
              fields: row.inputsOne,
              setValue,
              t,
            })}
          </div>
        ))}
      </div>
    </CustomPanel>
  )
}

export default PerceptorsTemplate180
